import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import {
  DeletePickFromSubmissionDoc,
  DeletePickFromPresentationSubmissionPublicDoc,
} from '@minecraft.graphql-operations';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { FeedbackSectionPick, HandleUpdateParams, HandleUpdateSubmissionFn } from './types';

export type UseDeletePickParams = {
  isPublic: boolean;
  /** id for the submission which should have the comment added */
  submissionId: string | number;
  /** this is set only in public presentation after the user has set a name for themselves */
  userName?: string;
  /** list of current picks */
  pickedBy: FeedbackSectionPick[] | readonly FeedbackSectionPick[];
  /** callback to update related feedback fields on other parent query data */
  handleUpdateSubmission?: HandleUpdateSubmissionFn;
  /** guid for the presentation folder (used on public presentation page) */
  folderGuid?: string;
} & Pick<HandleUpdateParams, 'talentEntityId'>;

export const usePickDelete = ({
  folderGuid,
  handleUpdateSubmission,
  isPublic,
  pickedBy,
  submissionId,
  talentEntityId,
  userName,
}: UseDeletePickParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const [mutationLoading, setMutationLoading] = useState(false);

  const [deletePickPrivate] = useMutation(DeletePickFromSubmissionDoc);
  const [deletePickPublic] = useMutation(DeletePickFromPresentationSubmissionPublicDoc);

  const onPickDelete = useCallback(
    async ({ pickId }: { pickId: string | number }): Promise<void> => {
      setMutationLoading(true);
      try {
        let response;
        if (!isPublic) {
          response = await deletePickPrivate({
            variables: {
              submissionId: talentEntityId || submissionId,
              pickId: Number(pickId),
            },
          });
        } else {
          response = await deletePickPublic({
            variables: {
              submissionId: talentEntityId || submissionId,
              pickId: pickId?.toString(),
              guid: folderGuid,
            },
          });
        }

        if (response?.errors?.length) {
          throw response.errors[0];
        }

        if (typeof handleUpdateSubmission === 'function') {
          handleUpdateSubmission({
            talentEntityId,
            deletedPick: pickedBy.find((p) => p?.id?.toString() === pickId?.toString()),
            pickedBy: pickedBy.filter((p) => p?.id?.toString() !== pickId?.toString()),
          });
        }

        showMessage({
          message: t('common:projectDetails.feedback.pickSuccessfullyDeleted'),
          color: 'success',
          sticky: true,
        });
      } catch (errorGraphQL) {
        captureExceptionEvent(errorGraphQL, {
          location: 'usePickDelete',
          userName,
          submissionId,
          pickId,
          isPublic: isPublic?.toString(),
        });

        showMessage({
          message: t('common:globalError.unexpectedServerShort'),
          color: 'danger',
          sticky: true,
        });
      }
      setMutationLoading(false);
    },
    [
      deletePickPrivate,
      deletePickPublic,
      folderGuid,
      handleUpdateSubmission,
      isPublic,
      pickedBy,
      showMessage,
      submissionId,
      t,
      talentEntityId,
      userName,
    ]
  );

  return useMemo(() => {
    return {
      onPickDelete,
      deletePickSubmitting: mutationLoading,
    };
  }, [onPickDelete, mutationLoading]);
};
